import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import '@/App.css';

// Pages
import LandingPage from '@/pages/LandingPage';
import AuthPage from '@/pages/AuthPage';
import AuthCallback from '@/pages/AuthCallback';
import VerifyEmail from '@/pages/VerifyEmail';
import ProfessionalDashboard from '@/pages/ProfessionalDashboard';
import CompanyDashboard from '@/pages/CompanyDashboard';
import ProfilePage from '@/pages/ProfilePage';
import ViewProfile from '@/pages/ViewProfile';
import BrowseListings from '@/pages/BrowseListings';
import ListingDetail from '@/pages/ListingDetail';
import CreateListing from '@/pages/CreateListing';
import EditListing from '@/pages/EditListing';
import MessagesPage from '@/pages/MessagesPage';
import FAQPage from '@/pages/FAQPage';
import ContactPage from '@/pages/ContactPage';
import PrivacyPage from '@/pages/PrivacyPage';
import GestionePrivacyPage from '@/pages/GestionePrivacyPage';
import AdminLogin from '@/pages/AdminLogin';
import AdminDashboard from '@/pages/AdminDashboard';
import AnalyticsDashboard from '@/pages/AnalyticsDashboard';
import FavoritesPage from '@/pages/FavoritesPage';
import SettingsPage from '@/pages/SettingsPage';
import SupportPage from '@/pages/SupportPage';
import ComplaintsPage from '@/pages/ComplaintsPage';

// Components
import CookieBanner from '@/components/CookieBanner';
import PageTracker from '@/components/PageTracker';


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthContext = React.createContext();

// Router wrapper that handles session_id in URL fragment
function AppRouter({ user }) {
  const location = useLocation();
  
  // CRITICAL: Check URL fragment for session_id SYNCHRONOUSLY during render
  // This prevents race conditions by processing new session_id FIRST before checking existing session
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth" element={user ? <Navigate to="/dashboard" /> : <AuthPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/contatti" element={<ContactPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/gestione-privacy" element={<GestionePrivacyPage />} />
      <Route path="/admin" element={<AdminLogin />} />
      <Route path="/admin/dashboard" element={<AdminDashboard />} />
      <Route
        path="/dashboard"
        element={
          user ? (
            user.user_type === 'professional' ? (
              <ProfessionalDashboard />
            ) : (
              <CompanyDashboard />
            )
          ) : (
            <Navigate to="/auth" />
          )
        }
      />
      <Route path="/profile" element={user ? <ProfilePage /> : <Navigate to="/auth" />} />
      <Route path="/view-profile" element={user ? <ViewProfile /> : <Navigate to="/auth" />} />
      <Route path="/listings" element={user ? <BrowseListings /> : <Navigate to="/auth" />} />
      <Route path="/listings/:id" element={user ? <ListingDetail /> : <Navigate to="/auth" />} />
      <Route path="/listings/:id/edit" element={user ? <EditListing /> : <Navigate to="/auth" />} />
      <Route path="/create-listing" element={user ? <CreateListing /> : <Navigate to="/auth" />} />
      <Route path="/messages" element={user ? <MessagesPage /> : <Navigate to="/auth" />} />
      <Route path="/messages/:conversationId" element={user ? <MessagesPage /> : <Navigate to="/auth" />} />
      <Route path="/analytics" element={user ? <AnalyticsDashboard /> : <Navigate to="/auth" />} />
      <Route path="/favorites" element={user ? <FavoritesPage /> : <Navigate to="/auth" />} />
      <Route path="/settings" element={user ? <SettingsPage /> : <Navigate to="/auth" />} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/complaints" element={user ? <ComplaintsPage /> : <Navigate to="/auth" />} />
    </Routes>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      setToken(storedToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching current user:', error);
      localStorage.removeItem('token');
      setToken(null);
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, token, login, logout }}>
      <BrowserRouter>
        <PageTracker />
        <AppRouter user={user} />
        <Toaster position="top-right" />
        {/* Cookie Banner - only show if not logged in */}
        {!user && <CookieBanner />}
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

export default App;
